import _isNil from 'lodash/isNil';
import _isDate from 'lodash/isDate';
import Parameter, {
  BoolClause,
  RangeClause,
  RangeCondition,
} from './Parameter';
import ParameterKeyValue from './ParameterKeyValue';
import { ResourceDateFilter } from '../configuredResourceTypes';

/**
 * Attempt to convert date value into string.  Returns null if value could not be converted
 *
 * @param {Date} date
 * @returns {string}
 */
function dateValueToString(date?: Date): string | null {
  if (_isNil(date) || !_isDate(date)) return null;

  return date.toISOString();
}

export type FilterDates = {
  min?: Date;
  max?: Date;
};

export default class DateFilter extends Parameter {
  private _applyDateFilter: boolean;
  private _resourceDateFilterMeta: ResourceDateFilter | null;
  private _gtDateString: string | null;
  private _ltDateString: string | null;

  constructor(
    resourceDateFilterMeta: ResourceDateFilter | null, // should always be populated, but since user configurable, allow for null
    dateFilter: FilterDates = {},
    applyDateFilter = true,
  ) {
    super();
    this._resourceDateFilterMeta = resourceDateFilterMeta;
    this._applyDateFilter = !!this._resourceDateFilterMeta && applyDateFilter;
    this._gtDateString = dateValueToString(dateFilter.min);
    this._ltDateString = dateValueToString(dateFilter.max);
  }

  get parameterKeyValue(): ParameterKeyValue {
    if (!this._applyDateFilter) return new ParameterKeyValue();

    const value: string[] = [];

    if (!_isNil(this._gtDateString)) {
      value.push(`gt${this._gtDateString}`);
    }

    if (!_isNil(this._ltDateString)) {
      value.push(`lt${this._ltDateString}`);
    }

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return new ParameterKeyValue(this._resourceDateFilterMeta.property, value);
  }

  async getFSSClause(): Promise<BoolClause | null> {
    const noDateSpecified =
      _isNil(this._gtDateString) && _isNil(this._ltDateString);
    const noDateSelectorsSpecified =
      !this._resourceDateFilterMeta?.dateSelector &&
      !this._resourceDateFilterMeta?.periodSelector;

    if (!this._applyDateFilter || noDateSpecified || noDateSelectorsSpecified)
      return null;

    const rangeCondition: RangeCondition = {
      format: 'strict_date_optional_time',
    };

    if (this._ltDateString) {
      rangeCondition.lte = this._ltDateString;
    }

    if (this._gtDateString) {
      rangeCondition.gte = this._gtDateString;
    }

    const rangeDocProps = [];
    if (this._resourceDateFilterMeta?.dateSelector) {
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      rangeDocProps.push(this._resourceDateFilterMeta.dateSelector);
    }

    if (this._resourceDateFilterMeta?.periodSelector) {
      rangeDocProps.push(
        // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
        `${this._resourceDateFilterMeta.periodSelector}.start`,
      );
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      rangeDocProps.push(`${this._resourceDateFilterMeta.periodSelector}.end`);
    }

    const rangeClauses: RangeClause[] = rangeDocProps.map<RangeClause>(_ => ({
      range: {
        [_]: rangeCondition,
      },
    }));

    return {
      bool: {
        should: rangeClauses,
      },
    };
  }
}
